import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Terminal } from "lucide-react";
import { useErrorCount } from "@/hooks/useAppLogs";
import { AppLogModal } from "./AppLogModal";

const HIDE_FAB_KEY = "app_logs_fab_hidden";

/**
 * Globalny floating button "Logi" w prawym dolnym rogu.
 * Klik → otwiera modal z pełną historią logów.
 * Badge: liczba ERROR-ów w ostatnich 24h.
 *
 * Mount w App.tsx (lub Layout). Można ukryć w Settings → toggle 'app_logs_fab_hidden'.
 */
export function AppLogFab() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { data: errorCount = 0 } = useErrorCount(24);

  // Read hide state from localStorage + listen for changes
  useEffect(() => {
    const read = () => {
      if (typeof window === "undefined") return;
      setHidden(localStorage.getItem(HIDE_FAB_KEY) === "true");
    };
    read();
    const onStorage = (e: StorageEvent) => {
      if (e.key === HIDE_FAB_KEY) read();
    };
    window.addEventListener("storage", onStorage);
    // Same-tab updates (storage event fires only cross-tab)
    const interval = setInterval(read, 1000);
    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(interval);
    };
  }, []);

  if (hidden) return null;

  const hasErrors = errorCount > 0;

  return (
    <>
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          variant="outline"
          className={`h-11 px-4 rounded-full shadow-lg bg-background/95 backdrop-blur border-2 hover:bg-accent relative gap-2 font-semibold transition-all ${
            hasErrors
              ? "border-destructive/70 text-destructive hover:border-destructive"
              : "border-primary/40 hover:border-primary/70"
          }`}
          onClick={() => setOpen(true)}
          title={`Logi aplikacji${hasErrors ? ` (${errorCount} błędów w ostatnich 24h)` : ""}`}
          aria-label="Otwórz panel logów"
        >
          <Terminal className="h-5 w-5" />
          <span className="text-sm uppercase tracking-wide">Logi</span>
          {hasErrors && (
            <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold ml-1">
              {errorCount > 99 ? "99+" : errorCount}
            </span>
          )}
        </Button>
      </div>
      <AppLogModal open={open} onOpenChange={setOpen} />
    </>
  );
}
